import { useState } from 'react';

/**
 * Multi-series line chart — completion rate across the inference concurrency sweep.
 * Three arms: the assembled baseline, an application-layer-only control, and the Fnk stack DialogMe runs on.
 * Emphasis form: accent for our arm, context gray for the two we are measured against.
 * Accent #6366f1 validated against the dark surface (#0f172a).
 */

const ACCENT = '#6366f1';
const CONTEXT = '#64748b';
const CONTEXT_DIM = '#475569';

const W = 560;
const H = 250;
const PAD_L = 52;
const PAD_R = 20;
const PAD_T = 20;
const PAD_B = 46;

const Y_MAX = 10;
const LEVELS = ['4', '8', '16', '32', '64'];

interface Series {
  label: string;
  sub: string;
  values: number[];
  color: string;
  dashed?: boolean;
  accent?: boolean;
}

const series: Series[] = [
  {
    label: 'Assembled stack',
    sub: 'Spring MVC + LangChain4j + vLLM',
    values: [1.1, 2.0, 3.24, 4.6, 4.76],
    color: CONTEXT,
  },
  {
    label: 'Application layer only, over vLLM',
    sub: 'the control arm — our application work, standard boundary to the engine',
    values: [1.1, 1.98, 3.22, 4.4, 5.32],
    color: CONTEXT_DIM,
    dashed: true,
  },
  {
    label: 'Fnk AI stack',
    sub: 'the stack DialogMe runs on — the contract continued through the boundary to the engine',
    values: [1.61, 2.72, 4.96, 7.72, 9.7],
    color: ACCENT,
    accent: true,
  },
];

function x(i: number) {
  return PAD_L + (i / (LEVELS.length - 1)) * (W - PAD_L - PAD_R);
}

function y(v: number) {
  return H - PAD_B - (v / Y_MAX) * (H - PAD_T - PAD_B);
}

function path(values: number[]) {
  return values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ');
}

export function ConcurrencySweepChart() {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label="Successful completion rate in requests per second across inference concurrency 4 to 64. The assembled stack rises from 1.10 to 4.76; the application-layer-only control from 1.10 to 5.32; the Fnk stack from 1.61 to 9.70, leading at every point."
      >
        {/* gridlines */}
        {[0, 2.5, 5, 7.5, 10].map((g) => (
          <g key={g}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={y(g)}
              y2={y(g)}
              stroke="#334155"
              strokeWidth="1"
              strokeDasharray={g === 0 ? undefined : '2 4'}
            />
            <text x={PAD_L - 10} y={y(g) + 4} textAnchor="end" fontSize="10" fill="#64748b">
              {g}
            </text>
          </g>
        ))}

        {/* x-axis labels */}
        {LEVELS.map((label, i) => (
          <text key={label} x={x(i)} y={H - PAD_B + 18} textAnchor="middle" fontSize="10" fill="#64748b">
            {label}
          </text>
        ))}
        <text x={(PAD_L + W - PAD_R) / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="#475569">
          Inference concurrency limit
        </text>

        {/* series */}
        {series.map((s) => {
          const dim = hovered !== null && hovered !== s.label;
          return (
            <g key={s.label} opacity={dim ? 0.3 : 1}>
              <path
                d={path(s.values)}
                fill="none"
                stroke={s.color}
                strokeWidth={s.accent ? 2.5 : 1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={s.dashed ? '4 4' : undefined}
              />
              {s.values.map((v, i) => (
                <circle key={i} cx={x(i)} cy={y(v)} r={s.accent ? 3.5 : 2.5} fill={s.color} />
              ))}
            </g>
          );
        })}

        {/* the number that carries the slide */}
        <text x={x(4) - 8} y={y(9.7) - 10} textAnchor="end" fontSize="11" fontWeight="600" fill="#a5b4fc">
          9.70
        </text>
        <text x={x(4) - 8} y={y(4.76) + 16} textAnchor="end" fontSize="11" fill="#94a3b8">
          4.76
        </text>
      </svg>

      {/* legend */}
      <div className="mt-4 space-y-2">
        {series.map((s) => (
          <div
            key={s.label}
            className="flex cursor-default items-baseline gap-2.5"
            onMouseEnter={() => setHovered(s.label)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(s.label)}
            onBlur={() => setHovered(null)}
            tabIndex={0}
          >
            <span
              className="mt-1.5 h-0.5 w-4 shrink-0 rounded-full"
              style={{
                background: s.color,
                opacity: hovered && hovered !== s.label ? 0.4 : 1,
              }}
              aria-hidden="true"
            />
            <p className="text-[11px] leading-relaxed">
              <span className={s.accent ? 'font-semibold text-slate-200' : 'text-slate-400'}>
                {s.label}
              </span>{' '}
              <span className="text-slate-600">— {s.sub}</span>
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 border-t border-slate-700/40 pt-4">
        <p className="text-[13px] leading-relaxed text-slate-300">
          <span className="font-semibold text-white">
            The lead holds at every point and widens with load.
          </span>{' '}
          Between concurrency 32 and 64 the assembled stack gained 3.5% — it had effectively stopped
          scaling — while the Fnk stack gained 25.6%. The curves are not converging.
        </p>
        <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
          Paired run: same model, same workload, same hardware; three machines across a 2.5GbE LAN,
          50 concurrent chat users. One run per stack per point — five points in the same direction,
          not a confidence interval.
        </p>
      </div>
    </figure>
  );
}
